import React from 'react';

import {
  FormControl,
  FormHelperText,
  Select,
  SelectProps,
} from '@mui/material';
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  Path,
} from 'react-hook-form';

import { MenuItem, Typography } from '@liscio/ui';

export interface InlineRichOptionSelectProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  data: RichOption[];
  error?: FieldError;
  placeholder?: string;
  selectProps?: SelectProps;
}

/**
 * Inline MUI Select for desktop-friendly forms.
 * Unlike firm `RHFSelect`, this does not open AutocompleteOverlaySelect (full-screen).
 */
export const InlineRichOptionSelect = <
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  data,
  error,
  placeholder = 'Select...',
  selectProps,
}: InlineRichOptionSelectProps<TFieldValues>) => {
  const menuProps = {
    PaperProps: { style: { maxHeight: 300 } },
    disableScrollLock: true,
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl fullWidth error={Boolean(error)}>
          <Select
            {...selectProps}
            {...field}
            fullWidth
            displayEmpty
            MenuProps={menuProps}
            value={field.value ?? ''}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography variant="body1" color="text.secondary">
                    {placeholder}
                  </Typography>
                );
              }
              const option = data.find((item) => item.value === selected);
              return option?.label ?? String(selected);
            }}
          >
            {data.map((option) => (
              <MenuItem
                key={String(option.value ?? '')}
                value={option.value ?? ''}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error?.message && (
            <FormHelperText>{error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default InlineRichOptionSelect;
